// app/javascript/modules/ferias/form_periodos.js
// Calcula dias de cada período, exibe contadores e erros de campo,
// controla visibilidade automática dos períodos 2 e 3, e emite
// ferias:total-changed com detalhe por período.

import { calcDias } from './form_helpers'

const PAIRS = [
  { num: 1, ini: '#params_inicio',          fim: '#params_termino'          },
  { num: 2, ini: '#params_inicio_ferias_2', fim: '#params_termino_ferias_2' },
  { num: 3, ini: '#params_inicio_ferias_3', fim: '#params_termino_ferias_3' },
]

export function initFeriasPeriodos(form) {
  const totalEl  = form.querySelector('#movimentacoes_pessoal_total_periodo_ferias')
  const periodo2 = form.querySelector('[data-ferias-periodo="2"]')
  const periodo3 = form.querySelector('[data-ferias-periodo="3"]')

  // ── Helpers ────────────────────────────────────────────────────────────────

  function getDiasAbono() {
    const cb = form.querySelector('#params_abono_pecuniario')
    if (!cb?.checked) return 0
    const v = parseInt(form.querySelector('#params_dias_a_serem_vendidos')?.value || '0', 10)
    return isNaN(v) ? 0 : Math.min(Math.max(v, 0), 10)
  }

  function getDiasDisponiveis() { return 30 - getDiasAbono() }

  function getPairDays(num) {
    const { ini, fim } = PAIRS[num - 1]
    const iniV = form.querySelector(ini)?.value || ''
    const fimV = form.querySelector(fim)?.value || ''
    if (!iniV || !fimV) return null
    return calcDias(iniV, fimV)  // returns -1 if fim < ini
  }

  function isPairEmpty(num) {
    const { ini, fim } = PAIRS[num - 1]
    return !form.querySelector(ini)?.value && !form.querySelector(fim)?.value
  }

  function setCounter(num, dias) {
    const el = form.querySelector(`#periodo_${num}_contador`)
    if (!el) return
    if (dias === null) {
      el.textContent = '— dias'
      el.className = 'text-xs font-medium text-gray-400'
    } else if (dias === -1) {
      el.textContent = '— dias'
      el.className = 'text-xs font-medium text-red-500'
    } else {
      el.textContent = `${dias} ${dias === 1 ? 'dia' : 'dias'}`
      el.className = 'text-xs font-medium text-blue-600'
    }
  }

  function setFieldError(num, msg) {
    const errEl = form.querySelector(`#periodo_${num}_erro`)
    const { ini, fim } = PAIRS[num - 1]
    const iniEl = form.querySelector(ini)
    const fimEl = form.querySelector(fim)
    if (msg) {
      if (errEl) { errEl.textContent = msg; errEl.classList.remove('hidden') }
      iniEl?.classList.add('border-red-400', 'bg-red-50')
      fimEl?.classList.add('border-red-400', 'bg-red-50')
    } else {
      if (errEl) { errEl.textContent = ''; errEl.classList.add('hidden') }
      iniEl?.classList.remove('border-red-400', 'bg-red-50')
      fimEl?.classList.remove('border-red-400', 'bg-red-50')
    }
  }

  function clearPeriodFields(num) {
    const { ini, fim } = PAIRS[num - 1]
    const iniEl = form.querySelector(ini)
    const fimEl = form.querySelector(fim)
    if (iniEl) iniEl.value = ''
    if (fimEl) fimEl.value = ''
    setCounter(num, null)
    setFieldError(num, null)
  }

  // ── Core calculation ───────────────────────────────────────────────────────

  // Rastreia qual período foi alterado por último pelo usuário (para mostrar
  // o erro de "soma ultrapassa 30 dias" no campo correto).
  let _lastChangedPeriod = null

  // Converte dd/mm/yyyy → Date (ou null)
  function parseBRDate(str) {
    if (!str) return null
    const m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    return m ? new Date(+m[3], +m[2] - 1, +m[1]) : null
  }

  // Verifica sobreposição entre os períodos visíveis e válidos (>= 5 dias).
  // Retorna array de { pair: [numA, numB] }.
  function checkOverlaps() {
    const p2Vis = !periodo2?.classList.contains('hidden')
    const p3Vis = !periodo3?.classList.contains('hidden')

    const ranges = PAIRS
      .filter((_, i) => i === 0 || (i === 1 && p2Vis) || (i === 2 && p3Vis))
      .map(({ num, ini, fim }) => {
        const iniDate = parseBRDate(form.querySelector(ini)?.value || '')
        const fimDate = parseBRDate(form.querySelector(fim)?.value || '')
        const days    = iniDate && fimDate ? Math.floor((fimDate - iniDate) / 86400000) + 1 : -1
        return { num, ini: iniDate, fim: fimDate, valid: days >= 5 }
      })
      .filter(r => r.valid)

    const overlaps = []
    for (let i = 0; i < ranges.length; i++) {
      for (let j = i + 1; j < ranges.length; j++) {
        const a = ranges[i], b = ranges[j]
        // Dois intervalos se sobrepõem se max(ini) <= min(fim)
        if (Math.max(a.ini, b.ini) <= Math.min(a.fim, b.fim)) {
          overlaps.push({ pair: [a.num, b.num] })
        }
      }
    }
    return overlaps
  }

  function calcularTotal() {
    const disponiveis = getDiasDisponiveis()
    const p2Vis = !periodo2?.classList.contains('hidden')
    const p3Vis = !periodo3?.classList.contains('hidden')
    let total = 0, hasInvalid = false
    let p1d = 0, p2d = 0, p3d = 0

    const d1 = getPairDays(1)
    if (d1 !== null) {
      if (d1 === -1) hasInvalid = true
      else { p1d = d1; total += d1 }
    }
    if (p2Vis) {
      const d2 = getPairDays(2)
      if (d2 !== null) {
        if (d2 === -1) hasInvalid = true
        else { p2d = d2; total += d2 }
      }
    }
    if (p3Vis) {
      const d3 = getPairDays(3)
      if (d3 !== null) {
        if (d3 === -1) hasInvalid = true
        else { p3d = d3; total += d3 }
      }
    }

    const erroTotalEl = form.querySelector('#erro-total-dias')

    if (totalEl) {
      totalEl.value = total || ''
      const over = total > 30
      totalEl.style.backgroundColor = over ? '#FFCCCC' : ''
      totalEl.classList.toggle('border-red-400', over)
      if (erroTotalEl) {
        if (over) {
          erroTotalEl.textContent = `O total de dias (${total}) ultrapassa os 30 dias permitidos. Ajuste os períodos.`
          erroTotalEl.classList.remove('hidden')
        } else {
          erroTotalEl.textContent = ''
          erroTotalEl.classList.add('hidden')
        }
      }
    }

    // Valida regra dos 14 dias (ao menos um período deve ter >= 14 dias)
    const $erro14 = form.querySelector('#erro-14-dias')
    if ($erro14) {
      const temFracionamento = (p2Vis && p2d > 0) || (p3Vis && p3d > 0)
      if (temFracionamento && total > 0) {
        const maxDias = Math.max(p1d, p2d, p3d)
        if (maxDias < 14) {
          $erro14.textContent = 'Pelo menos um período de férias deve ter no mínimo 14 dias corridos.'
          $erro14.classList.remove('hidden')
        } else {
          $erro14.textContent = ''
          $erro14.classList.add('hidden')
        }
      } else {
        $erro14.textContent = ''
        $erro14.classList.add('hidden')
      }
    }

    // Valida total de períodos > 30 (sem abono)
    // Mostra erro abaixo do campo sendo editado
    ;[1, 2, 3].forEach(n => {
      const el = form.querySelector(`#periodo_${n}_erro`)
      if (el && el.dataset.feriasOver30) {
        el.textContent = ''; el.classList.add('hidden'); delete el.dataset.feriasOver30
        form.querySelector(PAIRS[n-1].ini)?.classList.remove('border-red-400', 'bg-red-50')
        form.querySelector(PAIRS[n-1].fim)?.classList.remove('border-red-400', 'bg-red-50')
      }
    })

    if (total > 30 && _lastChangedPeriod !== null) {
      const errEl = form.querySelector(`#periodo_${_lastChangedPeriod}_erro`)
      if (errEl && !errEl.textContent) {
        errEl.textContent = `O total de dias (${total}) ultrapassa os 30 dias permitidos.`
        errEl.classList.remove('hidden')
        errEl.dataset.feriasOver30 = '1'
        const { ini, fim } = PAIRS[_lastChangedPeriod - 1]
        form.querySelector(ini)?.classList.add('border-red-400', 'bg-red-50')
        form.querySelector(fim)?.classList.add('border-red-400', 'bg-red-50')
      }
    }

    // Valida conflito períodos + abono > 30
    // O erro aparece no último período alterado pelo usuário (ou no campo de abono
    // quando a alteração veio de lá — sinalizado por _lastChangedPeriod === null).
    const erroAbono = form.querySelector('#erro-abono')
    const diasAbono = getDiasAbono()
    const soma = total + diasAbono

    // Limpa erro de abono e de todos os períodos antes de re-avaliar
    if (erroAbono) { erroAbono.textContent = ''; erroAbono.classList.add('hidden') }
    ;[1, 2, 3].forEach(n => {
      const el = form.querySelector(`#periodo_${n}_erro`)
      if (el && el.dataset.feriasOverflow) {
        el.textContent = ''; el.classList.add('hidden'); delete el.dataset.feriasOverflow
        form.querySelector(PAIRS[n-1].ini)?.classList.remove('border-red-400', 'bg-red-50')
        form.querySelector(PAIRS[n-1].fim)?.classList.remove('border-red-400', 'bg-red-50')
      }
    })

    if (diasAbono > 0 && total > 0 && soma > 30) {
      const msg = `Os períodos (${total} dias) + abono (${diasAbono} dias) = ${soma} dias. Não pode ultrapassar 30.`
      if (_lastChangedPeriod !== null) {
        // Mostra no último período alterado
        const errEl = form.querySelector(`#periodo_${_lastChangedPeriod}_erro`)
        if (errEl) {
          errEl.textContent = msg
          errEl.classList.remove('hidden')
          errEl.dataset.feriasOverflow = '1'
        }
        const { ini, fim } = PAIRS[_lastChangedPeriod - 1]
        form.querySelector(ini)?.classList.add('border-red-400', 'bg-red-50')
        form.querySelector(fim)?.classList.add('border-red-400', 'bg-red-50')
      } else if (erroAbono) {
        // Alteração veio do campo de abono — mostra ali
        erroAbono.textContent = msg
        erroAbono.classList.remove('hidden')
      }
    }

    // ── Valida sobreposição entre períodos ────────────────────────────────────
    // Limpa erros de sobreposição anteriores
    ;[1, 2, 3].forEach(n => {
      const el = form.querySelector(`#periodo_${n}_erro`)
      if (el && el.dataset.feriasOverlap) {
        el.textContent = ''; el.classList.add('hidden'); delete el.dataset.feriasOverlap
        form.querySelector(PAIRS[n-1].ini)?.classList.remove('border-red-400', 'bg-red-50')
        form.querySelector(PAIRS[n-1].fim)?.classList.remove('border-red-400', 'bg-red-50')
      }
    })

    const overlaps = checkOverlaps()
    overlaps.forEach(({ pair }) => {
      // Mostra no último período alterado (se ele for um dos envolvidos),
      // caso contrário no maior número da sobreposição.
      const targetNum = (_lastChangedPeriod && pair.includes(_lastChangedPeriod))
        ? _lastChangedPeriod
        : Math.max(...pair)
      const otherNum = pair.find(n => n !== targetNum)

      const errEl = form.querySelector(`#periodo_${targetNum}_erro`)
      // Só sobrescreve se não há outro erro (não-sobreposição) neste campo
      if (errEl && !errEl.textContent) {
        const ordinals = ['', '1º', '2º', '3º']
        errEl.textContent = `Esta data já está dentro do ${ordinals[otherNum]} período selecionado. Os períodos não podem se sobrepor.`
        errEl.classList.remove('hidden')
        errEl.dataset.feriasOverlap = '1'
        const { ini, fim } = PAIRS[targetNum - 1]
        form.querySelector(ini)?.classList.add('border-red-400', 'bg-red-50')
        form.querySelector(fim)?.classList.add('border-red-400', 'bg-red-50')
      }
    })

    form.dispatchEvent(new CustomEvent('ferias:total-changed', {
      bubbles: false,
      detail: { total, hasInvalid, p1: p1d, p2: p2d, p3: p3d, disponiveis },
    }))

    return total
  }

  // ── Per-period change handler ──────────────────────────────────────────────

  function onPeriodChange(num, fromUser = false) {
    if (fromUser) _lastChangedPeriod = num
    const disponiveis = getDiasDisponiveis()
    const { ini, fim } = PAIRS[num - 1]
    const iniV = form.querySelector(ini)?.value || ''
    const fimV = form.querySelector(fim)?.value || ''

    // Limpa erros gerenciados antes de reavaliar
    const errElNow = form.querySelector(`#periodo_${num}_erro`)
    if (errElNow?.dataset.feriasAntecedencia) {
      setFieldError(num, null)
      delete errElNow.dataset.feriasAntecedencia
    }
    if (errElNow?.dataset.feriasDiaSemana) {
      setFieldError(num, null)
      delete errElNow.dataset.feriasDiaSemana
    }

    // Valida dia da semana: não-12x36 não podem iniciar férias na sexta ou sábado
    const tipoEscalaEl = form.querySelector('#ferias_tipo_escala')
    const tipoEscala   = parseInt(tipoEscalaEl?.value || '0', 10)
    if (iniV && tipoEscala !== 3) {
      const iniDate = parseBRDate(iniV)
      if (iniDate) {
        const dow = iniDate.getDay() // 0=dom, 5=sex, 6=sab
        if (dow === 5 || dow === 6) {
          const nomeDia = dow === 5 ? 'sexta-feira' : 'sábado'
          const errEl = form.querySelector(`#periodo_${num}_erro`)
          if (errEl) {
            errEl.textContent = `Férias não podem iniciar em ${nomeDia} para esta escala de trabalho.`
            errEl.classList.remove('hidden')
            errEl.dataset.feriasDiaSemana = '1'
          }
          form.querySelector(ini)?.classList.add('border-red-400', 'bg-red-50')
          setCounter(num, null)
          calcularTotal()
          return
        }
      }
    }

    // Valida antecedência mínima de 30 dias para a data de início
    if (iniV) {
      const hoje = new Date(); hoje.setHours(0, 0, 0, 0)
      const minDate = new Date(hoje); minDate.setDate(minDate.getDate() + 30)
      const iniDate = parseBRDate(iniV)
      if (iniDate && iniDate < minDate) {
        const fmtMin = minDate.toLocaleDateString('pt-BR')
        const errEl = form.querySelector(`#periodo_${num}_erro`)
        if (errEl) {
          errEl.textContent = `A data de início deve ter no mínimo 30 dias de antecedência (a partir de ${fmtMin}).`
          errEl.classList.remove('hidden')
          errEl.dataset.feriasAntecedencia = '1'
        }
        form.querySelector(ini)?.classList.add('border-red-400', 'bg-red-50')
        setCounter(num, null)
        calcularTotal()
        return
      }
    }

    // Wait for both dates before processing
    if ((iniV && !fimV) || (!iniV && fimV)) return

    const dias = getPairDays(num)
    setCounter(num, (iniV && fimV) ? dias : null)

    if (!iniV && !fimV) {
      setFieldError(num, null)
      if (num === 1) {
        if (isPairEmpty(2)) { periodo2?.classList.add('hidden') }
        if (isPairEmpty(3)) { periodo3?.classList.add('hidden') }
      }
      calcularTotal()
      return
    }

    if (dias === -1) {
      setFieldError(num, 'A data de término não pode ser anterior à de início.')
      calcularTotal()
      return
    }

    if (num === 1) {
      if (dias < 5) {
        setFieldError(1, 'Cada período fracionado deve ter no mínimo 5 dias corridos.')
        if (isPairEmpty(2)) periodo2?.classList.add('hidden')
        if (isPairEmpty(3)) periodo3?.classList.add('hidden')
      } else if (dias >= disponiveis) {
        setFieldError(1, null)
        if (isPairEmpty(2)) { periodo2?.classList.add('hidden'); clearPeriodFields(2) }
        if (isPairEmpty(3)) { periodo3?.classList.add('hidden'); clearPeriodFields(3) }
      } else {
        setFieldError(1, null)
        periodo2?.classList.remove('hidden')
      }
    } else if (num === 2) {
      if (dias < 5) {
        setFieldError(2, 'Cada período fracionado deve ter no mínimo 5 dias corridos.')
        if (isPairEmpty(3)) periodo3?.classList.add('hidden')
      } else {
        const p1 = getPairDays(1) || 0
        const restante = disponiveis - p1 - dias
        if (restante > 0 && restante < 5) {
          setFieldError(2, `Este período deixa apenas ${restante} ${restante === 1 ? 'dia' : 'dias'} para o 3º período (mínimo 5 dias). Ajuste os períodos.`)
          if (isPairEmpty(3)) periodo3?.classList.add('hidden')
        } else {
          setFieldError(2, null)
          if (restante <= 0) {
            if (isPairEmpty(3)) { periodo3?.classList.add('hidden'); clearPeriodFields(3) }
          } else {
            periodo3?.classList.remove('hidden')
          }
        }
      }
    } else if (num === 3) {
      setFieldError(3, dias < 5 ? 'Cada período fracionado deve ter no mínimo 5 dias corridos.' : null)
    }

    calcularTotal()
  }

  // ── Event listeners ────────────────────────────────────────────────────────

  const _handlers = []

  PAIRS.forEach(({ num, ini, fim }) => {
    const iniEl = form.querySelector(ini)
    const fimEl = form.querySelector(fim)
    // 'change' atualiza _lastChangedPeriod (data realmente selecionada/editada).
    // 'blur'   recalcula sem atualizar _lastChangedPeriod (foco saiu, sem alteração).
    const changeHandler = () => onPeriodChange(num, true)
    const blurHandler   = () => onPeriodChange(num, false)
    iniEl?.addEventListener('change', changeHandler)
    iniEl?.addEventListener('blur',   blurHandler)
    fimEl?.addEventListener('change', changeHandler)
    fimEl?.addEventListener('blur',   blurHandler)
    _handlers.push([iniEl, 'change', changeHandler], [iniEl, 'blur', blurHandler])
    _handlers.push([fimEl, 'change', changeHandler], [fimEl, 'blur', blurHandler])
  })

  // Abono changes affect getDiasDisponiveis → recalculate all
  // Sinaliza _lastChangedPeriod = null para que o erro vá ao campo de abono.
  const abonoEl = form.querySelector('#params_abono_pecuniario')
  const diasEl  = form.querySelector('#params_dias_a_serem_vendidos')
  const abonoHandler = () => {
    _lastChangedPeriod = null
    onPeriodChange(1); onPeriodChange(2); onPeriodChange(3)
  }
  abonoEl?.addEventListener('change', abonoHandler)
  diasEl?.addEventListener('change',  abonoHandler)
  diasEl?.addEventListener('input',   abonoHandler)
  _handlers.push([abonoEl, 'change', abonoHandler])
  _handlers.push([diasEl, 'change', abonoHandler], [diasEl, 'input', abonoHandler])

  function apply() {
    onPeriodChange(1)
    onPeriodChange(2)
    onPeriodChange(3)
  }

  apply()

  return {
    apply,
    destroy() {
      _handlers.forEach(([el, evt, fn]) => el?.removeEventListener(evt, fn))
    },
  }
}
